import { useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { AssetRef, OwnerCommand, Workspace } from "../api/contract";
import type { PublishingController } from "../hooks/usePublishing";
import { DETAILS_GROUPS, SETTINGS_GROUPS, settingsValues, type SettingsGroup } from "../lib/siteSettings";
import { displayOrigin } from "../lib/workspaceState";
import { DomainView, PublishingPending } from "./DomainView";
import { ManagementModal } from "./ManagementModal";
import { formatPrice } from "./PublishFlow";
import { SiteDetailsView } from "./SiteDetailsView";
import { Spinner } from "./Spinner";

interface SiteSettingsProps {
  /** The group to open at. Without one, a wide screen opens Address and a phone opens the list. */
  initialGroup?: SettingsGroup;
  workspace: Workspace;
  publishing: PublishingController;
  /** The site's address as the workspace knows it, until the publishing status says more. */
  address: string;
  upload: (icon: Blob) => Promise<AssetRef>;
  photos?: Array<{ id: string; label: string; src: string; mediaType?: string }>;
  onSaveDetails: (command: Extract<OwnerCommand, { kind: "site.set" }>) => Promise<boolean>;
  /** Hands a request to Stax in the chat (the modal closes first). */
  onAskAdmin: (text: string) => void;
  onAddressChanged: () => void;
  onClose: () => void;
}

/** What the Plan group says: what Free and Pro each mean, and the way to Pro. There is no billing portal to link to. */
function PlanGroup({ publishing }: { publishing: PublishingController }) {
  const status = publishing.status;
  if (!status) return <PublishingPending publishing={publishing} />;
  if (status.entitled) {
    return <p className="bw-settings-plan"><strong>Pro:</strong> you can publish your site and use your own domain.</p>;
  }
  const monthly = status.prices?.monthly ? formatPrice(status.prices.monthly) : null;
  const annual = status.prices?.annual ? formatPrice(status.prices.annual) : null;
  const starting = publishing.checkout === "starting";
  return (
    <div className="bw-settings-plan-free">
      <p className="bw-settings-plan">
        <strong>Free:</strong> build and preview everything. Publishing and your own domain come with Pro{monthly ? `, from ${monthly} a month` : ""}.
      </p>
      <div className="bw-actions">
        <button type="button" className="bw-btn bw-btn--primary" onClick={() => void publishing.subscribe("monthly")} disabled={starting} aria-busy={starting}>{starting && <Spinner />}Get the Pro plan</button>
        {annual && <button type="button" className="bw-btn bw-btn--text" onClick={() => void publishing.subscribe("annual")} disabled={starting}>Pay yearly instead ({annual})</button>}
      </div>
      {publishing.checkout === "failed" && <p className="bw-item-note" role="alert">Checkout couldn’t start. Try again.</p>}
    </div>
  );
}

/**
 * Every setting that covers the whole site, in one modal: Address, Search and sharing, Language and time, Plan and
 * Analytics. Each nav row shows its current value, so the nav is also a summary of the site's state. On a phone the
 * nav is a list that drills into a group, and Back returns to it.
 */
export function SiteSettings({ initialGroup, workspace, publishing, address, upload, photos, onSaveDetails, onAskAdmin, onAddressChanged, onClose }: SiteSettingsProps) {
  // null is the phone's list; a wide screen shows Address then.
  const [group, setGroup] = useState<SettingsGroup | null>(initialGroup ?? null);
  const [live, setLive] = useState<{ title: string; icon: string } | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const pane = useRef<HTMLDivElement>(null);
  const rows = useRef(new Map<SettingsGroup, HTMLButtonElement>());
  const shown: SettingsGroup = group ?? "address";
  const current = SETTINGS_GROUPS.find((entry) => entry.id === shown)!;

  const status = publishing.status;
  const seo = workspace.seo;
  const values = settingsValues({ seo, domain: status?.domain, platformAddress: status ? displayOrigin(status.origin) : address, entitled: status?.entitled });
  const title = live?.title ?? seo?.title?.trim() ?? seo?.defaults.title ?? workspace.siteName ?? address;
  const icon = live?.icon ?? seo?.icon?.src ?? seo?.defaults.icon;

  const open = (next: SettingsGroup) => {
    setGroup(next);
    pane.current?.scrollTo({ top: 0 });
    requestAnimationFrame(() => heading.current?.focus({ preventScroll: true }));
  };
  const back = () => {
    const from = group;
    setGroup(null);
    if (from) requestAnimationFrame(() => rows.current.get(from)?.focus({ preventScroll: true }));
  };

  return (
    <ManagementModal title="Site settings" onClose={onClose} flush back={group ? { label: "Site settings", onClick: back } : undefined}>
      <div className="bw-settings" data-view={group ? "pane" : "list"}>
        <div className="bw-settings-identity" role="group" aria-label="This site">
          {icon && <img src={icon} alt="" width={28} height={28} />}
          <span className="bw-settings-who">
            <strong>{title}</strong>
            <span className="bw-settings-address">{values.address}</span>
            {status && <span className="bw-settings-chip" data-plan={status.entitled ? "pro" : "free"}>{status.entitled ? "Pro" : "Free"}</span>}
          </span>
        </div>
        <div className="bw-settings-main">
          <nav className="bw-settings-nav" aria-label="Site settings groups">
            {SETTINGS_GROUPS.map((entry) => (
              <button
                key={entry.id}
                ref={(element) => { if (element) rows.current.set(entry.id, element); else rows.current.delete(entry.id); }}
                type="button"
                className="bw-settings-row"
                data-selected={shown === entry.id || undefined}
                aria-current={shown === entry.id ? "true" : undefined}
                onClick={() => open(entry.id)}
              >
                <span><strong>{entry.label}</strong><small>{values[entry.id]}</small></span>
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            ))}
          </nav>
          <div className="bw-settings-pane" ref={pane}>
            <h2 ref={heading} tabIndex={-1}>{current.label}</h2>
            <p className="bw-settings-lede">{current.lede}</p>
            {shown === "address" && (
              <DomainView
                workspace={workspace}
                publishing={publishing}
                onAskAdmin={onAskAdmin}
                onAddressChanged={onAddressChanged}
              />
            )}
            {shown === "plan" && <PlanGroup publishing={publishing} />}
            {/* One form for the three groups that save together: mounted throughout, so what was typed survives a visit to another group. */}
            <div className="bw-settings-details" hidden={!DETAILS_GROUPS.includes(shown)}>
              {seo ? (
                <SiteDetailsView
                  seo={seo}
                  address={values.address}
                  upload={upload}
                  photos={photos}
                  onSave={onSaveDetails}
                  onAsk={() => onAskAdmin("Write a search title and description for my site and each page")}
                  group={shown === "language" || shown === "analytics" ? shown : "search"}
                  onLive={setLive}
                />
              ) : <p className="bw-library-intro">These are available once your first draft is built.</p>}
            </div>
          </div>
        </div>
      </div>
    </ManagementModal>
  );
}
